import { Box, CircularProgress, DialogProps, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AppContext } from '../../app/context';
import { Button, Dialog } from '../../components';
import usePartnerGeogroupQuery from '../../hooks/use-partner-geogroup-query';
import useUpdateGeogroupMutation from '../../hooks/use-update-geogroup-mutation';
import SizeRangeForm, { sizeRanges } from '../../pages/admin/data/size-range-form';

function CompanySizeDialog({
  ...props
}: Omit<DialogProps, 'onClose'> & { onClose: () => void }): React.JSX.Element {
  const [sizeRangeIndex, setSizeRangeIndex] = useState<number | null>(0);
  const [isSubmitting, setSubmitting] = useState(false);
  const {
    partner: { current: currentPartner },
  } = useContext(AppContext);
  const { data: geogroup } = usePartnerGeogroupQuery(currentPartner);
  const { mutateAsync: updateGeogroup } = useUpdateGeogroupMutation();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  async function handleSubmit() {
    if (!geogroup || !currentPartner) return;

    setSubmitting(true);

    const index = sizeRangeIndex ?? 0;
    const { min, max } = sizeRanges[index];

    try {
      await updateGeogroup({ id: geogroup.id, partner: currentPartner, min, max });

      props.onClose();
    } catch (err) {
      console.error(err);
      enqueueSnackbar(t('companies.size_dialog.server_error'), { variant: 'error' });
    }

    setSubmitting(false);
  }

  return (
    <Dialog disableBackgroundClick disableCloseButton fullWidth maxWidth="md" {...props}>
      <Box display="flex" flexDirection="column" gap={3}>
        <Typography fontSize="1.25rem" fontWeight={600}>
          {t('companies.size_dialog.title')}
        </Typography>
        <Typography variant="body2">{t('companies.size_dialog.description')}</Typography>
        <SizeRangeForm
          disabled={isSubmitting}
          setSizeRangeIndex={setSizeRangeIndex}
          sizeRangeIndex={sizeRangeIndex}
        />
        <Box alignItems="center" display="flex" gap={3} justifyContent="flex-end">
          <Button
            color="primary"
            disabled={isSubmitting}
            onClick={handleSubmit}
            startIcon={isSubmitting && <CircularProgress color="inherit" size={16} thickness={4} />}
            variant="contained"
          >
            OK
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
}

export default CompanySizeDialog;
